import { z } from 'zod';

import { ALERT_SEVERITIES, REFERENCE_KINDS } from '../review/schema';

/**
 * The one field the note list needs from `backend/app/api/lists.py`'s `AlertPage` - how many
 * alerts are still open, for the badge (`docs/implementation/06-mobile-web.md` 6.3: "just a
 * badge/indicator pointing at" the full inbox, which is 6.5's screen). `zod` objects ignore
 * unknown keys by default, so this stays valid even though the real response also carries
 * `items` and `next_cursor`.
 */
export const openAlertCountResponseSchema = z.object({
  open_count: z.number(),
});

export type OpenAlertCountResponse = z.infer<typeof openAlertCountResponseSchema>;

/** One durable owner-visible alert (`AlertResponse`), the full shape the 6.5 inbox needs -
 * unlike the peek above, this carries `note_id`/`reference_issue_id` (for navigating back to
 * the exact note and field) and `resolved_at` (for the open/resolved filter). */
export const alertSchema = z.object({
  id: z.string(),
  type: z.string(),
  severity: z.enum(ALERT_SEVERITIES),
  message_key: z.string(),
  note_id: z.string().nullable(),
  reference_issue_id: z.string().nullable(),
  /** Which list `reference_issue_id` names a missing row in, and the literal text the note read
   * for it - `null` alongside `reference_issue_id` for every alert that names no issue. Lets
   * `alertMessageLabel` say *which* customer or concept a `reference_issue.open` alert is about,
   * instead of every open one on a note reading identically. */
  reference_kind: z.enum(REFERENCE_KINDS).nullable(),
  reference_observed_text: z.string().nullable(),
  created_at: z.string(),
  resolved_at: z.string().nullable(),
});
export type Alert = z.infer<typeof alertSchema>;

/** `GET /alerts` - a page of alerts plus how many are still open, mirroring `AlertPage`. */
export const alertPageSchema = z.object({
  items: z.array(alertSchema),
  next_cursor: z.string().nullable(),
  open_count: z.number(),
});
export type AlertPage = z.infer<typeof alertPageSchema>;
